import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useEffect, useRef } from "react";

import type { MockAITemplateType } from "../../src/utils/generateExcalidrawTemplate";

export type AIGenerationMode = "api" | "mock";
export type AIGenerationInsertMode = "replace" | "append-below" | "new-board";

const generatorTypes: Array<{
  value: MockAITemplateType;
  label: string;
}> = [
  { value: "presentation", label: "Apresentação visual" },
  { value: "sales-funnel", label: "Funil de vendas" },
  { value: "process", label: "Processo" },
  { value: "mind-map", label: "Mapa mental" },
  { value: "customer-journey", label: "Jornada do cliente" },
  { value: "crm", label: "CRM" },
];

export const AIMockGeneratorDialog = ({
  prompt,
  type,
  mode,
  isGenerating,
  error,
  insertMode,
  onPromptChange,
  onTypeChange,
  onModeChange,
  onInsertModeChange,
  useDrawLibrary,
  drawLibraryCategory,
  drawLibraryCategories,
  drawLibraryStatus,
  drawLibraryError,
  onUseDrawLibraryChange,
  onDrawLibraryCategoryChange,
  onGenerate,
  onUseLocalFallback,
  onClose,
}: {
  prompt: string;
  type: MockAITemplateType;
  mode: AIGenerationMode;
  isGenerating: boolean;
  error: string | null;
  insertMode: AIGenerationInsertMode;
  useDrawLibrary: boolean;
  drawLibraryCategory: string;
  drawLibraryCategories: Array<{ value: string; label: string }>;
  drawLibraryStatus: "idle" | "loading" | "ready" | "error";
  drawLibraryError: string | null;
  onPromptChange: (prompt: string) => void;
  onTypeChange: (type: MockAITemplateType) => void;
  onModeChange: (mode: AIGenerationMode) => void;
  onInsertModeChange: (mode: AIGenerationInsertMode) => void;
  onUseDrawLibraryChange: (useLibrary: boolean) => void;
  onDrawLibraryCategoryChange: (category: string) => void;
  onGenerate: () => void | Promise<void>;
  onUseLocalFallback: () => void | Promise<void>;
  onClose: () => void;
}) => {
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = promptTextareaRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 280)}px`;
  }, [prompt]);

  return (
    <Dialog
      title="Gerar com IA"
      size="small"
      onCloseRequest={onClose}
      closeOnClickOutside
    >
      <form
        className="ai-mock-generator"
        onSubmit={(event) => {
          event.preventDefault();
          onGenerate();
        }}
      >
        <label className="ai-mock-generator__field">
          <span>Prompt</span>
          <textarea
            ref={promptTextareaRef}
            className="ai-mock-generator__prompt"
            value={prompt}
            disabled={isGenerating}
            onChange={(event) => onPromptChange(event.target.value)}
            placeholder="Crie um funil de vendas para clínica estética"
            rows={5}
          />
        </label>

        <label className="ai-mock-generator__field">
          <span>Tipo</span>
          <select
            value={type}
            disabled={isGenerating}
            onChange={(event) =>
              onTypeChange(event.target.value as MockAITemplateType)
            }
          >
            {generatorTypes.map((generatorType) => (
              <option key={generatorType.value} value={generatorType.value}>
                {generatorType.label}
              </option>
            ))}
          </select>
        </label>

        <label className="ai-mock-generator__field">
          <span>Modo de geração</span>
          <select
            value={mode}
            disabled={isGenerating}
            onChange={(event) =>
              onModeChange(event.target.value as AIGenerationMode)
            }
          >
            <option value="api">IA real</option>
            <option value="mock">Mock local</option>
          </select>
        </label>

        <label className="ai-mock-generator__field">
          <span>Inserir como</span>
          <select
            value={insertMode}
            disabled={isGenerating}
            onChange={(event) =>
              onInsertModeChange(event.target.value as AIGenerationInsertMode)
            }
          >
            <option value="replace">Substituir canvas atual</option>
            <option value="append-below">
              Inserir abaixo do conteudo atual
            </option>
            <option value="new-board">Criar novo board</option>
          </select>
        </label>

        <label className="ai-mock-generator__checkbox">
          <input
            checked={useDrawLibrary}
            disabled={isGenerating}
            onChange={(event) =>
              onUseDrawLibraryChange(event.target.checked)
            }
            type="checkbox"
          />
          <span>Usar Biblioteca Draw</span>
        </label>

        {useDrawLibrary && (
          <label className="ai-mock-generator__field">
            <span>Categoria da Biblioteca Draw</span>
            <select
              value={drawLibraryCategory}
              disabled={isGenerating}
              onChange={(event) =>
                onDrawLibraryCategoryChange(event.target.value)
              }
            >
              {drawLibraryCategories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {useDrawLibrary && drawLibraryStatus === "loading" && (
          <div className="ai-mock-generator__hint">
            Carregando catalogo da Biblioteca Draw...
          </div>
        )}

        {useDrawLibrary && drawLibraryError && (
          <div className="ai-mock-generator__error">
            <span>{drawLibraryError}</span>
          </div>
        )}

        {error && (
          <div className="ai-mock-generator__error">
            <span>{error}</span>
            <button
              className="ai-mock-generator__link"
              type="button"
              disabled={isGenerating}
              onClick={onUseLocalFallback}
            >
              Usar template local
            </button>
          </div>
        )}

        <div className="ai-mock-generator__actions">
          <button
            className="ai-mock-generator__secondary"
            type="button"
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancelar
          </button>
          <button
            className="ai-mock-generator__primary"
            disabled={!prompt.trim() || isGenerating}
            type="submit"
          >
            {isGenerating ? "Gerando..." : "Gerar no canvas"}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
